import React, { useState, useEffect } from 'react';
import { Card, Table, Tag, Typography, Row, Col, Button, Spin, message, Space, Select, DatePicker, Input, Modal, Statistic } from 'antd';
import { ReloadOutlined, SearchOutlined, DollarOutlined } from '@ant-design/icons';
import { listTemporaryUsers } from '../services/userService';
import { getTemporaryUserPayments, markTemporaryPaymentAsPaid, getTemporaryPaymentReport } from '../services/attendanceService';
import { themeConfig } from '../config/theme';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { Search, TextArea } = Input;
const { RangePicker } = DatePicker;

const TemporaryUserPayments = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [temporaryUsers, setTemporaryUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paying, setPaying] = useState({});
  const [dateRange, setDateRange] = useState([dayjs().subtract(30, 'days'), dayjs()]);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    fetchTemporaryUsers();
  }, []);

  useEffect(() => {
    if (selectedUser) {
      fetchPayments();
    }
  }, [selectedUser, dateRange]);

  const fetchTemporaryUsers = async () => {
    setLoading(true);
    try {
      const response = await listTemporaryUsers();
      setTemporaryUsers(response.users || []);
    } catch (error) {
      console.error('Error fetching temporary users:', error);
      messageApi.error('Failed to fetch temporary users');
    } finally {
      setLoading(false);
    }
  };

  const fetchPayments = async () => {
    if (!selectedUser) return;
    setLoading(true);
    try {
      const [startDate, endDate] = dateRange;
      const response = await getTemporaryUserPayments(
        selectedUser,
        startDate.format('YYYY-MM-DD'),
        endDate.format('YYYY-MM-DD')
      );
      setPayments(response.payments || []);
    } catch (error) {
      console.error('Error fetching payments:', error);
      messageApi.error('Failed to fetch payments');
    } finally {
      setLoading(false);
    }
  };

  const [markPaymentModalVisible, setMarkPaymentModalVisible] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [paymentNarration, setPaymentNarration] = useState('');

  const handleMarkAsPaid = async (mobileNumber, date) => {
    setSelectedPayment({ mobileNumber, date });
    setPaymentNarration('');
    setMarkPaymentModalVisible(true);
  };

  const handleConfirmMarkAsPaid = async () => {
    if (!selectedPayment) return;
    
    setPaying(prev => ({ ...prev, [`${selectedPayment.mobileNumber}-${selectedPayment.date}`]: true }));
    try {
      await markTemporaryPaymentAsPaid(selectedPayment.mobileNumber, selectedPayment.date, paymentNarration || null);
      messageApi.success('Payment marked as paid successfully');
      fetchPayments();
      setMarkPaymentModalVisible(false);
      setSelectedPayment(null);
      setPaymentNarration('');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to mark payment as paid';
      messageApi.error(errorMessage);
    } finally {
      setPaying(prev => {
        const newState = { ...prev };
        if (selectedPayment) {
          delete newState[`${selectedPayment.mobileNumber}-${selectedPayment.date}`];
        }
        return newState;
      });
    }
  };

  const handleViewReport = async (mobileNumber) => {
    setLoading(true);
    try {
      const response = await getTemporaryPaymentReport(mobileNumber);
      const report = response.report;
      
      Modal.info({
        title: (() => {
          const user = temporaryUsers.find(u => u.MobileNumber === mobileNumber);
          if (!user) return `Payment Report - ${mobileNumber}`;
          return `Payment Report - ${user.FullName}${user.Designation ? ` (${user.Designation})` : ''}`;
        })(),
        width: 800,
        content: (
          <div>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={6}>
                <Card>
                  <Statistic title="Total Days" value={report.totalDays} />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic title="Paid Days" value={report.paidDays} valueStyle={{ color: themeConfig.status.success }} />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic title="Unpaid Days" value={report.unpaidDays} valueStyle={{ color: themeConfig.status.error }} />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic title="Total Amount" value={`₹${report.totalAmount.toFixed(2)}`} />
                </Card>
              </Col>
            </Row>
            <Table
              columns={[
                { title: 'Date', dataIndex: 'date', key: 'date' },
                { title: 'Amount', dataIndex: 'amount', key: 'amount', render: (amt) => `₹${amt.toFixed(2)}` },
                { title: 'Status', dataIndex: 'status', key: 'status' },
                { title: 'Site', dataIndex: 'siteId', key: 'siteId' },
                { title: 'Payment Status', dataIndex: 'paymentStatus', key: 'paymentStatus', 
                  render: (status) => <Tag color={status === 'PAID' ? 'green' : 'orange'}>{status}</Tag> },
                { title: 'Paid At', dataIndex: 'paidAt', key: 'paidAt', render: (date) => date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-' },
                { title: 'Paid By', dataIndex: 'paidBy', key: 'paidBy', render: (by) => by ? by.replace('ADMIN#', '').replace('USER#', '') : '-' },
              ]}
              dataSource={report.records}
              pagination={{ pageSize: 10 }}
              rowKey="date"
            />
          </div>
        ),
      });
    } catch (error) {
      console.error('Error fetching payment report:', error);
      messageApi.error('Failed to fetch payment report');
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = temporaryUsers.filter(user =>
    user.FullName?.toLowerCase().includes(searchText.toLowerCase()) ||
    user.MobileNumber?.toLowerCase().includes(searchText.toLowerCase())
  );

  const unpaidPayments = payments.filter(p => p.PaymentStatus === 'UNPAID' || !p.PaymentStatus);
  const paidPayments = payments.filter(p => p.PaymentStatus === 'PAID');

  const columns = [
    {
      title: 'Date',
      dataIndex: 'Date',
      key: 'date',
      render: (date) => dayjs(date).format('DD MMM, YYYY'),
      sorter: (a, b) => dayjs(a.Date).unix() - dayjs(b.Date).unix(),
    },
    {
      title: 'Status',
      dataIndex: 'Status',
      key: 'status',
    },
    {
      title: 'Amount',
      dataIndex: 'Amount',
      key: 'amount',
      render: (amount) => amount ? `₹${amount.toFixed(2)}` : '₹0.00',
      sorter: (a, b) => (a.Amount || 0) - (b.Amount || 0),
    },
    {
      title: 'Site',
      dataIndex: 'SiteName',
      key: 'siteName',
      render: (siteName, record) => siteName || record.SiteId || 'N/A',
    },
    {
      title: 'Marked By',
      dataIndex: 'MarkedByName',
      key: 'markedByName',
      render: (markedByName, record) => markedByName || (record.MarkedBy ? record.MarkedBy.replace('ADMIN#', '').replace('USER#', '') : 'N/A'),
    },
    {
      title: 'Payment Status',
      dataIndex: 'PaymentStatus',
      key: 'paymentStatus',
      render: (status) => {
        const paymentStatus = status || 'UNPAID';
        return <Tag color={paymentStatus === 'PAID' ? 'green' : 'orange'}>{paymentStatus}</Tag>;
      },
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => {
        const isPaid = record.PaymentStatus === 'PAID';
        const mobileNumber = record.PK ? record.PK.replace('TEMP_USER#', '') : selectedUser;
        const paymentKey = `${mobileNumber}-${record.Date}`;
        return (
          <Space>
            {!isPaid && (
              <Button
                type="primary"
                size="small"
                icon={<DollarOutlined />}
                loading={paying[paymentKey]}
                onClick={() => handleMarkAsPaid(mobileNumber, record.Date)}
              >
                Pay
              </Button>
            )}
            {isPaid && (
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Paid on {record.PaidAt ? dayjs(record.PaidAt).format('DD MMM, YYYY') : 'N/A'}
                {record.PaidByName && ` by ${record.PaidByName}`}
                {!record.PaidByName && record.PaidBy && ` by ${record.PaidBy.replace('ADMIN#', '').replace('USER#', '')}`}
              </Text>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      {contextHolder}
      <Card>
        <Title level={2}>Temporary User Payments</Title>
        
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={12}>
            <Search
              placeholder="Search temporary users"
              onChange={e => setSearchText(e.target.value)}
              style={{ width: '100%' }}
            />
          </Col>
          <Col span={12}>
            <Select
              placeholder="Select Temporary User"
              style={{ width: '100%' }}
              value={selectedUser}
              onChange={setSelectedUser}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            >
              {filteredUsers.map((user) => {
                const displayName = `${user.FullName}${user.Designation ? ` (${user.Designation})` : ''} - ${user.MobileNumber}`;
                return (
                  <Option key={user.MobileNumber} value={user.MobileNumber} label={displayName}>
                    {displayName}
                  </Option>
                );
              })}
            </Select>
          </Col>
        </Row>

        {selectedUser && (
          <>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <RangePicker
                  value={dateRange}
                  onChange={(dates) => {
                    if (dates && dates.length === 2) {
                      setDateRange(dates);
                    }
                  }}
                  style={{ width: '100%' }}
                />
              </Col>
              <Col span={12}>
                <Space>
                  <Button icon={<ReloadOutlined />} onClick={fetchPayments}>
                    Refresh
                  </Button>
                  <Button 
                    type="default" 
                    onClick={() => handleViewReport(selectedUser)}
                    disabled={!selectedUser}
                  >
                    View Full Report
                  </Button>
                </Space>
              </Col>
            </Row>

            <Spin spinning={loading}>
              <Title level={4}>Unpaid Days ({unpaidPayments.length})</Title>
              <Table
                columns={columns}
                dataSource={unpaidPayments}
                rowKey={(record) => `${record.PK}-${record.Date}`}
                pagination={{ pageSize: 10 }}
                style={{ marginBottom: 24 }}
              />

              <Title level={4}>Paid Days ({paidPayments.length})</Title>
              <Table
                columns={columns.filter(col => col.key !== 'action')}
                dataSource={paidPayments}
                rowKey={(record) => `${record.PK}-${record.Date}`}
                pagination={{ pageSize: 10 }}
              />
            </Spin>
          </>
        )}

        {!selectedUser && (
          <div style={{ textAlign: 'center', padding: '40px', color: themeConfig.text.muted }}>
            <Text>Please select a temporary user to view payments</Text>
          </div>
        )}
      </Card>

      {/* Mark Payment as Paid Modal */}
      <Modal
        title="Mark Payment as Paid"
        visible={markPaymentModalVisible}
        onOk={handleConfirmMarkAsPaid}
        onCancel={() => {
          setMarkPaymentModalVisible(false);
          setSelectedPayment(null);
          setPaymentNarration('');
        }}
        okText="Mark as Paid"
        cancelText="Cancel"
      >
        {selectedPayment && (
          <div>
            <p>Mark payment for <strong>{selectedPayment.date}</strong> as paid?</p>
            <div style={{ marginTop: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                Narration (Optional):
              </label>
              <TextArea
                placeholder="Enter narration..."
                value={paymentNarration}
                onChange={(e) => setPaymentNarration(e.target.value)}
                rows={3}
                maxLength={200}
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default TemporaryUserPayments;

